import { ReactNode, useCallback, useRef } from "react";
import { motion, useAnimation, PanInfo } from "framer-motion";
import { triggerHaptic } from "@/hooks/use-haptics";

interface SwipeContainerProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Disable swiping in a direction (e.g., can't go to future) */
  disableSwipeLeft?: boolean;
  disableSwipeRight?: boolean;
  className?: string;
}

const SWIPE_THRESHOLD = 50;
const SWIPE_VELOCITY_THRESHOLD = 500;
const RESISTANCE = 0.3;

export function SwipeContainer({
  children,
  onSwipeLeft,
  onSwipeRight,
  disableSwipeLeft = false,
  disableSwipeRight = false,
  className = "",
}: SwipeContainerProps) {
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDrag = useCallback(
    (_: unknown, info: PanInfo) => {
      const { offset } = info;
      let x = offset.x;

      // Apply resistance at edges
      if ((x > 0 && disableSwipeRight) || (x < 0 && disableSwipeLeft)) {
        x = x * RESISTANCE;
      }

      controls.set({ x });
    },
    [controls, disableSwipeLeft, disableSwipeRight]
  );

  const handleDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      const { offset, velocity } = info;

      const swipedRight =
        (offset.x > SWIPE_THRESHOLD || velocity.x > SWIPE_VELOCITY_THRESHOLD) &&
        !disableSwipeRight;
      const swipedLeft =
        (offset.x < -SWIPE_THRESHOLD || velocity.x < -SWIPE_VELOCITY_THRESHOLD) &&
        !disableSwipeLeft;

      if (swipedRight && onSwipeRight) {
        // Animate out to right, then snap back
        triggerHaptic("light");
        controls
          .start({
            x: window.innerWidth,
            transition: { type: "spring", stiffness: 300, damping: 30 },
          })
          .then(() => {
            onSwipeRight();
            controls.set({ x: -window.innerWidth });
            controls.start({
              x: 0,
              transition: { type: "spring", stiffness: 300, damping: 30 },
            });
          });
      } else if (swipedLeft && onSwipeLeft) {
        // Animate out to left, then snap back
        triggerHaptic("light");
        controls
          .start({
            x: -window.innerWidth,
            transition: { type: "spring", stiffness: 300, damping: 30 },
          })
          .then(() => {
            onSwipeLeft();
            controls.set({ x: window.innerWidth });
            controls.start({
              x: 0,
              transition: { type: "spring", stiffness: 300, damping: 30 },
            });
          });
      } else {
        // Snap back to center
        controls.start({
          x: 0,
          transition: { type: "spring", stiffness: 500, damping: 35 },
        });
      }
    },
    [controls, disableSwipeLeft, disableSwipeRight, onSwipeLeft, onSwipeRight]
  );

  return (
    <div ref={containerRef} className={`overflow-hidden ${className}`}>
      <motion.div
        animate={controls}
        drag="x"
        dragDirectionLock
        dragElastic={0.1}
        dragConstraints={{ left: 0, right: 0 }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={{ touchAction: "pan-y" }}
        className="will-change-transform"
      >
        {children}
      </motion.div>
    </div>
  );
}
